import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useUserSettingsStore from '../stores/userSettingsStore';
import useAuthStore from '../stores/authStore';
import './UserSettings.css';

function UserSettings() {
  const { t } = useTranslation();
  const { 
    keyPoints, 
    isLoading, 
    error, 
    fetchSettings, 
    updateKeyPoints, 
    resetSettings,
    addKeyPoint,
    removeKeyPoint,
    editKeyPoint,
    clearError,
    closeSettings
  } = useUserSettingsStore();

  const { logout } = useAuthStore();
  
  const [newKeyPoint, setNewKeyPoint] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [initialKeyPoints, setInitialKeyPoints] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await fetchSettings();
      if (settings) {
        setInitialKeyPoints(settings.key_points || []);
      }
    };
    loadSettings();
  }, [fetchSettings]);

  // Отслеживаем изменения в ключевых пунктах
  useEffect(() => {
    if (initialKeyPoints.length > 0) {
      const changed = JSON.stringify(initialKeyPoints) !== JSON.stringify(keyPoints);
      setHasChanges(changed);
    }
  }, [keyPoints, initialKeyPoints]);

  const handleAddKeyPoint = () => {
    if (newKeyPoint.trim()) {
      if (keyPoints.length >= 10) {
        // Показываем сообщение об ошибке, если достигнут лимит
        set({ error: t('errors.maxKeyPoints') });
        return;
      }
      addKeyPoint(newKeyPoint);
      setNewKeyPoint('');
    }
  };

  const handleSave = async () => {
    try {
      await updateKeyPoints(keyPoints);
      closeSettings();
    } catch (err) {
      // Ошибка уже обработана в хранилище
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      t('userSettings.resetConfirm')
    );
    
    if (confirmed) {
      try {
        const settings = await resetSettings();
        if (settings) {
          setInitialKeyPoints(settings.key_points || []);
          setHasChanges(false);
        }
      } catch (err) {
        // Ошибка уже обработана в хранилище
      }
    }
  };

  const handleLogout = () => {
    closeSettings();
    logout();
  };

  return (
    <div className="settings-page">
      <div className="settings-content">
        <div className="settings-header">
          <h2>{t('userSettings.title')}</h2>
          <button className="settings-logout-button" onClick={handleLogout}>
            <svg viewBox="0 0 24 24">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
            {t('userSettings.logout')}
          </button>
        </div>
        
        {error && (
          <div className="settings-error">
            {error}
            <button className="clear-error" onClick={clearError}>×</button>
          </div>
        )}
        
        <div className="settings-section">
          <h3>{t('userSettings.keyInfoTitle')}</h3>
          <p>{t('userSettings.keyInfoDescPart1')}<b>{t('userSettings.keyInfoDescBold')}</b>{t('userSettings.keyInfoDescPart2')}</p>
          
          <div className="key-points-list">
            {keyPoints.map((point, index) => (
              <div key={index} className="key-point-item">
                <input
                  type="text"
                  value={editingIndex === index ? editingText : point}
                  onChange={(e) => {
                    if (editingIndex === index) {
                      setEditingText(e.target.value);
                    } else {
                      setEditingIndex(index);
                      setEditingText(e.target.value);
                    }
                  }}
                  onBlur={() => {
                    if (editingIndex === index && editingText.trim()) {
                      editKeyPoint(index, editingText);
                      setEditingIndex(null);
                    } else if (editingIndex === index) {
                      setEditingIndex(null);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && editingIndex === index && editingText.trim()) {
                      editKeyPoint(index, editingText);
                      setEditingIndex(null);
                    }
                  }}
                  onFocus={() => {
                    if (editingIndex !== index) {
                      setEditingIndex(index);
                      setEditingText(point);
                    }
                  }}
                />
                <button 
                  className="remove-key-point" 
                  onClick={() => removeKeyPoint(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          <div className="add-key-point">
            <input
              type="text"
              value={newKeyPoint}
              onChange={(e) => setNewKeyPoint(e.target.value)}
              placeholder={t('userSettings.addPlaceholder')}
              onKeyPress={(e) => e.key === 'Enter' && handleAddKeyPoint()}
              disabled={keyPoints.length >= 10}
            />
            <button 
              onClick={handleAddKeyPoint}
              disabled={keyPoints.length >= 10}
            >
              {t('userSettings.addButton')}
            </button>
          </div>
          {keyPoints.length >= 10 && (
            <div className="key-points-limit-message">
              {t('userSettings.maxReached')}
            </div>
          )}
        </div>
        
        <div className="settings-actions">
          <div className="settings-actions-left">
            <button 
              className="reset-button" 
              onClick={handleReset}
              disabled={isLoading}
            >
              {t('userSettings.resetButton')}
            </button>
          </div>
          <div className="settings-actions-right">
            <button 
              className="close-button" 
              onClick={closeSettings}
            >
              {hasChanges ? t('userSettings.cancel') : t('userSettings.close')}
            </button>
            <button
              className="save-button"
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
            >
              {t('userSettings.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserSettings; 
